/**
 * Pure helpers for the "My Usage" pace summary, the daily-spend calendar and the month-over-month
 * comparison. Every date is a `YYYY-MM-DD` key read as UTC, and "today" is always derived from the
 * response's `generatedAt` (never the real clock), so the numbers shown here agree with the
 * forecast chart and stay stable under test.
 */

import type { MyConsumptionResponse } from "../../api/reporting";

const DayMs = 86_400_000;

/** One cell of the daily-spend calendar's colour scale: 0 is no spend, 4 is the period's heaviest day. */
export type SpendLevel = 0 | 1 | 2 | 3 | 4;

/** The inputs {@link computeUsagePace} and {@link projectMonthEnd} need for one period and one spend figure. */
export interface UsagePaceInput {
  periodStart: string;
  periodEnd: string;
  generatedAt: string;
  amount: number;
  weekdayRatePerDay: number;
  weekendRatePerDay: number;
}

/** The pace figures for the current month against its allowance. Percentages are unrounded (0-100). */
export interface UsagePace {
  percentUsed: number;
  percentElapsed: number;
  projectedMonthEnd: number;
  remaining: number;
  remainingWorkingDays: number;
  headroomPerWorkingDay: number;
}

/** The direction and whole-number size of a change against a previous total. */
export interface MonthChange {
  direction: "up" | "down" | "same";
  percent: number;
}

/**
 * The pace inputs for the consolidated total of a loaded response. Shared by the summary line and
 * the month-over-month comparison so both read the same projection.
 *
 * @param response - The loaded consumption response.
 * @returns The pace input for the consolidated amount, or null when there is no consolidated figure.
 */
export function consolidatedPaceInput(response: MyConsumptionResponse): UsagePaceInput | null {
  const consolidated = response.consolidated;
  if (!consolidated) {
    return null;
  }
  return {
    periodStart: response.periodStart,
    periodEnd: response.periodEnd,
    generatedAt: response.generatedAt,
    amount: consolidated.amount,
    weekdayRatePerDay: consolidated.weekdayRatePerDay ?? consolidated.burnRatePerDay,
    weekendRatePerDay: consolidated.weekendRatePerDay ?? consolidated.burnRatePerDay,
  };
}

/**
 * Parses a `YYYY-MM-DD` key as the UTC instant at its midnight.
 *
 * @param isoDate - The calendar date to parse.
 * @returns Milliseconds since the epoch at UTC midnight of that date.
 */
function utcMidnight(isoDate: string): number {
  return Date.parse(`${isoDate}T00:00:00Z`);
}

/**
 * Formats an instant as its UTC calendar date key.
 *
 * @param ms - Milliseconds since the epoch.
 * @returns The `YYYY-MM-DD` key of that UTC date.
 */
function keyOf(ms: number): string {
  return new Date(ms).toISOString().slice(0, 10);
}

/**
 * Whether a UTC midnight falls on a Saturday or Sunday.
 *
 * @param ms - Milliseconds since the epoch at UTC midnight.
 * @returns True for Saturday and Sunday.
 */
function isWeekendDay(ms: number): boolean {
  const dayOfWeek = new Date(ms).getUTCDay();
  return dayOfWeek === 0 || dayOfWeek === 6;
}

/**
 * The whole number of days from one UTC midnight to another.
 *
 * @param fromMs - The earlier instant.
 * @param toMs - The later instant.
 * @returns The day difference, which is zero for the same day.
 */
function daysBetween(fromMs: number, toMs: number): number {
  return Math.round((toMs - fromMs) / DayMs);
}

/**
 * The "today" key for a period: the UTC date of `generatedAt`, clamped into the period so a
 * response generated after month end reads as the last day, and one generated before it as the first.
 *
 * @param periodStart - The first day of the period (`YYYY-MM-DD`).
 * @param periodEnd - The last day of the period (`YYYY-MM-DD`).
 * @param generatedAt - The ISO instant the response was generated, treated as "now".
 * @returns The clamped `YYYY-MM-DD` key for today.
 */
export function todayKeyFor(periodStart: string, periodEnd: string, generatedAt: string): string {
  const start = utcMidnight(periodStart);
  const end = utcMidnight(periodEnd);
  const generatedDay = utcMidnight(keyOf(Date.parse(generatedAt)));
  return keyOf(Math.min(Math.max(generatedDay, start), end));
}

/**
 * Projects a month-end total: the amount so far plus the weekday or weekend rate for each day after
 * today through the period end. Shared by the pace summary and the month-over-month comparison so
 * they never disagree with the forecast chart's projection.
 *
 * @param input - The period, the "now" instant, the amount so far and the two projected daily rates.
 * @returns The projected total at the period end.
 */
export function projectMonthEnd(input: UsagePaceInput): number {
  const todayMs = utcMidnight(todayKeyFor(input.periodStart, input.periodEnd, input.generatedAt));
  const endMs = utcMidnight(input.periodEnd);
  let projected = input.amount;
  for (let dayMs = todayMs + DayMs; dayMs <= endMs; dayMs += DayMs) {
    projected += isWeekendDay(dayMs) ? input.weekendRatePerDay : input.weekdayRatePerDay;
  }
  return projected;
}

/**
 * Works out how the current month's spend compares with its allowance: how much is used, how far
 * through the month we are, where the month is projected to end, and how much can be spent per
 * remaining working day while staying within the allowance.
 *
 * Remaining working days count Monday to Friday from today through the period end, both inclusive.
 * When none remain (a weekend at month end), the remaining calendar days are used instead, with a minimum of one.
 *
 * @param input - The period, the "now" instant, the amount so far and the projected daily rates.
 * @param allowance - The allowance in the same currency as `input.amount`.
 * @returns The pace figures for the period.
 */
export function computeUsagePace(input: UsagePaceInput, allowance: number): UsagePace {
  const startMs = utcMidnight(input.periodStart);
  const endMs = utcMidnight(input.periodEnd);
  const todayMs = utcMidnight(todayKeyFor(input.periodStart, input.periodEnd, input.generatedAt));
  const daysInPeriod = Math.max(1, daysBetween(startMs, endMs) + 1);
  const daysElapsed = daysBetween(startMs, todayMs) + 1;

  let remainingWorkingDays = 0;
  for (let dayMs = todayMs; dayMs <= endMs; dayMs += DayMs) {
    if (!isWeekendDay(dayMs)) {
      remainingWorkingDays += 1;
    }
  }
  if (remainingWorkingDays === 0) {
    remainingWorkingDays = Math.max(1, daysBetween(todayMs, endMs) + 1);
  }

  const remaining = allowance - input.amount;
  return {
    percentUsed: allowance > 0 ? (input.amount / allowance) * 100 : 0,
    percentElapsed: (daysElapsed / daysInPeriod) * 100,
    projectedMonthEnd: projectMonthEnd(input),
    remaining,
    remainingWorkingDays,
    headroomPerWorkingDay: remaining > 0 ? remaining / remainingWorkingDays : 0,
  };
}

/**
 * Lays a reporting period out as calendar weeks for the daily-spend heatmap. Weeks start on Monday;
 * slots before the first day or after the last day are null, so every week has seven slots.
 *
 * @param periodStart - The first day of the period (`YYYY-MM-DD`).
 * @param periodEnd - The last day of the period (`YYYY-MM-DD`).
 * @returns The weeks in order, each an array of seven `YYYY-MM-DD` keys or nulls for padding.
 */
export function calendarWeeks(periodStart: string, periodEnd: string): (string | null)[][] {
  const startMs = utcMidnight(periodStart);
  const endMs = utcMidnight(periodEnd);
  const mondayOffset = (new Date(startMs).getUTCDay() + 6) % 7;
  const firstMondayMs = startMs - mondayOffset * DayMs;

  const weeks: (string | null)[][] = [];
  for (let weekStartMs = firstMondayMs; weekStartMs <= endMs; weekStartMs += 7 * DayMs) {
    const week: (string | null)[] = [];
    for (let slot = 0; slot < 7; slot += 1) {
      const dayMs = weekStartMs + slot * DayMs;
      week.push(dayMs >= startMs && dayMs <= endMs ? keyOf(dayMs) : null);
    }
    weeks.push(week);
  }
  return weeks;
}

/**
 * The heatmap level for one day's spend, by quartile of the period's largest day. No spend is level
 * 0. Otherwise a day at or below a quarter of the maximum is level 1, at or below a half is 2, at or
 * below three quarters is 3, and anything above that is 4. A positive amount with no positive maximum
 * is treated as level 4.
 *
 * @param amount - The day's spend.
 * @param max - The largest single-day spend in the period.
 * @returns The level, from 0 to 4.
 */
export function spendLevel(amount: number, max: number): SpendLevel {
  if (amount <= 0) {
    return 0;
  }
  if (max <= 0) {
    return 4;
  }
  const ratio = amount / max;
  if (ratio <= 0.25) {
    return 1;
  }
  if (ratio <= 0.5) {
    return 2;
  }
  if (ratio <= 0.75) {
    return 3;
  }
  return 4;
}

/**
 * The change from a previous total to a current one, as a direction and a whole-number percentage.
 * A change under one percent is "same". No change is reported when there is nothing to compare
 * against: a null or zero previous total.
 *
 * @param current - The current total (the actual total, or the projected month-end).
 * @param previous - The previous month's total, or null when it is not known.
 * @returns The change, or null when there is no basis to compare against.
 */
export function monthChange(current: number, previous: number | null): MonthChange | null {
  if (previous === null || previous <= 0) {
    return null;
  }
  const ratio = (current - previous) / previous;
  if (Math.abs(ratio) < 0.01) {
    return { direction: "same", percent: 0 };
  }
  return { direction: ratio > 0 ? "up" : "down", percent: Math.round(Math.abs(ratio) * 100) };
}

/**
 * The first day of the month before the one that starts on `periodStart`.
 *
 * @param periodStart - The first day of the current period (`YYYY-MM-DD`).
 * @returns The `YYYY-MM-DD` key for the previous month's first day.
 */
export function previousMonthStart(periodStart: string): string {
  const start = new Date(utcMidnight(periodStart));
  return keyOf(Date.UTC(start.getUTCFullYear(), start.getUTCMonth() - 1, 1));
}

/**
 * The long English month name for a period's first day, read as UTC, for example `"September"`.
 *
 * @param isoDate - The date whose month name is wanted (`YYYY-MM-DD`).
 * @returns The full month name in en-US.
 */
export function formatMonthName(isoDate: string): string {
  return new Date(utcMidnight(isoDate)).toLocaleDateString("en-US", { month: "long", timeZone: "UTC" });
}
